import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog,
  DialogTrigger,
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@artstorefronts/ui';
import { toast } from 'sonner';
import { Settings2Icon } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../context/auth';
import { UPDATE_USER } from '../graphql/mutations';
import MyVoiceForm from './MyVoiceForm';
import { PROFILE_QUERY } from '../graphql/queries';
import { useState } from 'react';
import { useAnalytics } from '../context/analytics';
import { MyVoice, UserProfile } from '~/types';

interface FormData {
  myVoice?: MyVoice;
}

export function MyVoiceWrapper() {
  const [open, setOpen] = useState(false);
  const { user: currentUser } = useAuth();
  const { data, loading } = useQuery<{ user: UserProfile }>(PROFILE_QUERY, {
    fetchPolicy: 'network-only',
    variables: { asfId: currentUser?.user_id },
  });

  if (loading) return null;

  return (
    <>
      <MyVoiceDialog open={open} setOpen={setOpen} user={data?.user} />
    </>
  );
}

function MyVoiceDialog({
  user,
  open,
  setOpen,
}: {
  user: UserProfile | undefined;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { t } = useTranslation('chat');
  const { t: tc } = useTranslation('common');
  const { user: currentUser, refetchProfile } = useAuth();
  const { amplitude } = useAnalytics();

  const [updateUser] = useMutation(UPDATE_USER);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
    watch,
  } = useForm<FormData>({
    defaultValues: {
      myVoice: {
        formality: user?.voiceFormalities?.[0],
        tones: user?.voiceTones,
        avoidWords: user?.voiceAvoidWords?.[0]?.words || '',
      },
    },
  });

  const handleFormalityChange = (value: any) => {
    setValue('myVoice.formality', value);
  };

  const handleTonesChange = (value: any[]) => {
    setValue('myVoice.tones', value);
  };

  const handleAvoidWordsChange = (value: any) => {
    setValue('myVoice.avoidWords', value);
  };

  async function onSubmit(values: FormData) {
    try {
      const voiceFormality = values.myVoice?.formality;
      const voiceFormalities = voiceFormality ? [voiceFormality] : [];
      const voiceTones = values.myVoice?.tones ? values.myVoice.tones : [];
      const voiceAvoidWords = values.myVoice?.avoidWords;

      const input: {
        asfId: string;
        voiceTones?: string[];
        voiceFormalities?: string[];
        voiceAvoidWords?: string | null;
      } = {
        asfId: currentUser?.user_id,
      };

      if (voiceTones.length > 0) {
        input.voiceTones = voiceTones.map((tone) => tone.id);
      }

      if (voiceFormalities.length > 0) {
        input.voiceFormalities = voiceFormalities.map((formality) => formality.id);
      }

      // Emptying the field clears the words; the API leaves them alone when the field is omitted.
      input.voiceAvoidWords = voiceAvoidWords || null;

      await updateUser({ variables: { input } });
      await refetchProfile();

      amplitude.track('custom_voice_saved', {
        formality_type: voiceFormalities.map((formality) => formality.name.toLowerCase()) as any,
        tone_type: voiceTones.map((tone) => tone.name.toLowerCase()),
        avoid_words: voiceAvoidWords ?? '',
      });

      toast.success(t('profile-updated'), {
        position: 'top-right',
      });

      setOpen(false);
    } catch (error) {
      toast.error(t('failed-to-update-profile'), {
        position: 'top-right',
      });
      console.error(error);
    }
  }

  // TODO: render issue here, avoid words not updating without watch
  watch();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" className="h-6 text-xs" disabled={isSubmitting}>
          <Settings2Icon className="size-4" />
          {t('tune-my-voice')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Settings2Icon className="w-6 h-6 mr-2" /> {t('tune-my-voice')}
          </DialogTitle>
          <DialogDescription className="text-left">{t('voice-description')}</DialogDescription>
        </DialogHeader>
        {/* TODO: wire up onChange handlers */}
        <Controller
          name="myVoice"
          control={control}
          render={({ field }) => {
            return (
              <MyVoiceForm
                selectedFormality={field.value?.formality ?? null}
                onChangeFormality={handleFormalityChange}
                selectedTones={field.value?.tones || []}
                onChangeTones={handleTonesChange}
                avoidWords={field.value?.avoidWords ?? ''}
                onChangeAvoidWords={handleAvoidWordsChange}
              />
            );
          }}
        />
        <DialogFooter>
          <Button type="button" onClick={handleSubmit(onSubmit)}>
            {isSubmitting ? tc('saving') : tc('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MyVoiceDialog;
